import type { UserStatus } from 'nexgen-types';

// TODO(NGE-988): this is a verbatim copy of resolveNotificationBucket in
// nexgen-types/src/utils/notification-bucket.util.ts, duplicated here only because
// the shared package is consumed from #main and the export is not published yet.
// Once it is, delete everything below and re-export from 'nexgen-types' instead —
// the signature is identical, so no call site needs to change. The backend keeps
// its own copy at nexgen-services-nestjs/src/shared/utils/notification-bucket.util.ts;
// the two MUST stay in sync or server and client will disagree about which
// preference row applies, which is the exact class of bug NGE-988 exists to fix.

/**
 * The three preference buckets shown in Workplace Settings → Notifications.
 * Each corresponds to one card in that UI: Active/Available, Away/Idle/Break, Offline.
 */
export type NotificationBucket = 'online' | 'away' | 'offline';

/** The notification rows in the preference matrix. */
export type NotificationTypeKey = 'room' | 'guests' | 'messages';

/** The channel columns in the preference matrix. */
export interface NotificationChannels {
  app: boolean;
  browser: boolean;
  email: boolean;
  sms: boolean;
  mobile: boolean;
  whatsapp: boolean;
}

/** The subset of channels the client renders itself: in-app toast + sound, and the OS bubble. */
export type ClientNotificationChannels = Pick<NotificationChannels, 'app' | 'browser'>;

/** Logged-in statuses that mean "present, but not at the desk". */
const AWAY_STATUSES: readonly string[] = ['break', 'idle'];

/**
 * Resolve which notification preference bucket applies to a user.
 *
 * `socketConnected` — not `status` — is the authority for the offline bucket.
 * Presence is frequently stale: a shut laptop or a killed browser can keep
 * reporting `active` for minutes, and routing on that drops the notification
 * into the Active bucket with no live connection to deliver it on, so it is
 * lost entirely. A user with no socket is offline by definition, whatever
 * presence claims.
 *
 * Status only distinguishes Active from Away among *connected* users. Every
 * logged-in state other than break/idle is Active — including `meeting`,
 * `busy` and `locked`, which mean "present and working", not "away". An
 * explicit `offline` status is still honoured so "appear offline" works.
 */
export function resolveNotificationBucket(
  status: UserStatus | string | null | undefined,
  socketConnected: boolean
): NotificationBucket {
  if (!socketConnected) return 'offline';
  if (status === 'offline') return 'offline';
  if (status && AWAY_STATUSES.includes(status)) return 'away';
  return 'online';
}
